<template>
  <standard-dialog
    v-model="model"
    :title="$t('link_supplier_restaurant')"
    icon="mdi-link-variant"
    :save-text="$t('save')"
    :loading="saving"
    :disabled="!hasSupplierSelection || !hasRestaurantSelection"
    @save="save"
  >
    <!-- Supplier -->
    <div class="mb-1">
      <autocomplete
        v-model="selectedSupplierId"
        :items="allSupplierItems"
        :label="$t('supplier')"
        item-text="name"
        item-value="id"
        :disabled="!!fixedSupplier"
        :loading="loadingSuppliers"
        :multiple="!!fixedRestaurant"
        :chips="!!fixedRestaurant"
        :deletable-chips="!!fixedRestaurant"
        clearable
        @update:search-input="supplierSearch = $event"
      />
      <v-fade-transition>
        <div v-if="showAddSupplier" class="d-flex align-center mt-1 pl-1">
          <v-icon small color="primary" class="mr-1">mdi-plus-circle-outline</v-icon>
          <span class="caption grey--text text--darken-1 mr-2">
            {{ $t("add") }}: <em>{{ supplierSearch }}</em>
          </span>
          <v-btn
            x-small
            outlined
            color="primary"
            :loading="addingSupplier"
            @click="addNewSupplier"
          >
            {{ $t("add") }}
          </v-btn>
        </div>
      </v-fade-transition>
    </div>

    <v-divider class="my-3" />

    <!-- Restaurant -->
    <div class="mb-1">
      <autocomplete
        v-model="selectedRestaurantId"
        :items="allRestaurantItems"
        :label="$t('restaurant')"
        item-text="name"
        item-value="id"
        :disabled="!!fixedRestaurant"
        :loading="loadingRestaurants"
        :multiple="!!fixedSupplier"
        :chips="!!fixedSupplier"
        :deletable-chips="!!fixedSupplier"
        clearable
        @update:search-input="restaurantSearch = $event"
      />
      <v-fade-transition>
        <div v-if="showAddRestaurant" class="d-flex align-center mt-1 pl-1">
          <v-icon small color="primary" class="mr-1">mdi-plus-circle-outline</v-icon>
          <span class="caption grey--text text--darken-1 mr-2">
            {{ $t("add") }}: <em>{{ restaurantSearch }}</em>
          </span>
          <v-btn
            x-small
            outlined
            color="primary"
            :loading="addingRestaurant"
            @click="addNewRestaurant"
          >
            {{ $t("add") }}
          </v-btn>
        </div>
      </v-fade-transition>
    </div>
  </standard-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import warehouseService from "@/services/warehouse";
import restaurantService from "@/services/restaurant";
import StandardDialog from "@/components/dialogs/StandardDialog.vue";

export default {
  name: "SupplierRestaurantLinkDialog",
  mixins: [hasModel],
  components: {
    StandardDialog,
  },
  props: {
    /**
     * When provided, the supplier field is read-only (supplier side).
     * The user selects a restaurant.
     */
    fixedSupplier: {
      type: Object,
      default: null,
    },
    /**
     * When provided, the restaurant field is read-only (restaurant side).
     * The user selects a supplier.
     */
    fixedRestaurant: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      selectedSupplierId: null,
      selectedRestaurantId: null,

      suppliers: [],
      restaurants: [],
      extraSuppliers: [],
      extraRestaurants: [],

      loadingSuppliers: false,
      loadingRestaurants: false,
      addingSupplier: false,
      addingRestaurant: false,
      saving: false,

      supplierSearch: "",
      restaurantSearch: "",
    };
  },

  computed: {
    allSupplierItems() {
      return [...this.suppliers, ...this.extraSuppliers];
    },
    allRestaurantItems() {
      return [...this.restaurants, ...this.extraRestaurants];
    },
    hasRestaurantSelection() {
      if (this.fixedSupplier) {
        return (
          Array.isArray(this.selectedRestaurantId) && this.selectedRestaurantId.length > 0
        );
      }
      return !!this.selectedRestaurantId;
    },
    hasSupplierSelection() {
      if (this.fixedRestaurant) {
        return (
          Array.isArray(this.selectedSupplierId) && this.selectedSupplierId.length > 0
        );
      }
      return !!this.selectedSupplierId;
    },

    showAddSupplier() {
      if (this.fixedSupplier) return false;
      const q = (this.supplierSearch || "").trim();
      if (!q) return false;
      return !this.allSupplierItems.some(
        (s) => s.name?.toLowerCase() === q.toLowerCase()
      );
    },
    showAddRestaurant() {
      if (this.fixedRestaurant) return false;
      const q = (this.restaurantSearch || "").trim();
      if (!q) return false;
      return !this.allRestaurantItems.some(
        (r) => r.name?.toLowerCase() === q.toLowerCase()
      );
    },
  },

  watch: {
    model(val) {
      if (val) {
        this.init();
      } else {
        this.reset();
      }
    },
  },

  methods: {
    init() {
      if (this.fixedSupplier) {
        this.selectedSupplierId = this.fixedSupplier.id;
        this.suppliers = [this.fixedSupplier];
        this.selectedRestaurantId = [];
        Promise.all([this.fetchRestaurants(), this.fetchLinkedRestaurants()]);
      } else {
        this.fetchSuppliers();
      }

      if (this.fixedRestaurant) {
        this.selectedRestaurantId = this.fixedRestaurant.id;
        this.restaurants = [this.fixedRestaurant];
        this.selectedSupplierId = [];
        Promise.all([this.fetchSuppliers(), this.fetchLinkedSuppliers()]);
      } else if (!this.fixedSupplier) {
        this.fetchRestaurants();
      }
    },

    reset() {
      this.selectedSupplierId = this.fixedRestaurant ? [] : null;
      this.selectedRestaurantId = this.fixedSupplier ? [] : null;
      this.extraSuppliers = [];
      this.extraRestaurants = [];
      this.supplierSearch = "";
      this.restaurantSearch = "";
      this.saving = false;
    },

    async fetchSuppliers() {
      this.loadingSuppliers = true;
      try {
        const { data } = await warehouseService.getSuppliers();
        this.suppliers = Array.isArray(data) ? data : data?.data ?? [];
      } catch {
        // silent
      } finally {
        this.loadingSuppliers = false;
      }
    },

    async fetchRestaurants() {
      this.loadingRestaurants = true;
      try {
        const { data } = await restaurantService.getAllRestaurants();
        this.restaurants = Array.isArray(data) ? data : data?.data ?? [];
      } catch {
        // silent
      } finally {
        this.loadingRestaurants = false;
      }
    },

    async fetchLinkedRestaurants() {
      try {
        const { data } = await warehouseService.getSupplierRestaurants(
          this.fixedSupplier.id
        );
        const linked = Array.isArray(data) ? data : data?.data ?? [];
        this.selectedRestaurantId = linked.map((r) => r.id);
      } catch {
        // silent
      }
    },

    async fetchLinkedSuppliers() {
      try {
        const { data } = await restaurantService.getRestaurantSuppliers(
          this.fixedRestaurant.id
        );
        const linked = Array.isArray(data) ? data : data?.data ?? [];
        this.selectedSupplierId = linked.map((s) => s.id);
      } catch {
        // silent
      }
    },

    addNewSupplier() {
      const name = (this.supplierSearch || "").trim();
      if (!name) return;
      const newItem = { id: `new_supplier_${Date.now()}`, name, _new: true };
      this.extraSuppliers.push(newItem);
      if (this.fixedRestaurant) {
        this.selectedSupplierId = [...(this.selectedSupplierId || []), newItem.id];
      } else {
        this.selectedSupplierId = newItem.id;
      }
      this.supplierSearch = "";
    },

    addNewRestaurant() {
      const name = (this.restaurantSearch || "").trim();
      if (!name) return;
      const newItem = { id: `new_restaurant_${Date.now()}`, name, _new: true };
      this.extraRestaurants.push(newItem);
      if (this.fixedSupplier) {
        this.selectedRestaurantId = [...(this.selectedRestaurantId || []), newItem.id];
      } else {
        this.selectedRestaurantId = newItem.id;
      }
      this.restaurantSearch = "";
    },

    async save() {
      if (this.fixedSupplier) {
        const ids = (this.selectedRestaurantId || []).filter(
          (id) => !String(id).startsWith("new_")
        );
        if (!ids.length) return;
        this.saving = true;
        try {
          await warehouseService.attachRestaurantsToSupplier(this.fixedSupplier.id, ids);
          this.$emit("saved");
          this.model = false;
        } catch {
          // silent
        } finally {
          this.saving = false;
        }
      } else if (this.fixedRestaurant) {
        const ids = (this.selectedSupplierId || []).filter(
          (id) => !String(id).startsWith("new_")
        );
        if (!ids.length) return;
        this.saving = true;
        try {
          await restaurantService.attachSuppliersToRestaurant(
            this.fixedRestaurant.id,
            ids
          );
          this.$emit("saved");
          this.model = false;
        } catch {
          // silent
        } finally {
          this.saving = false;
        }
      }
    },
  },
};
</script>
